// AppRouter.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'src/components/ui/toaster';
import GoogleCallback from 'src/components/auth/GoogleCallback';
import { getToken, getUser } from 'src/utils/authStorage';
import MainLayout from '../../layouts/MainLayout/MainLayout';
import AuthLayout from '../../pages/AuthLayout/AuthLayout';
import SubmitResume from '../../pages/SubmitResume/SubmitResume';
import { CreateAccount } from '../../pages/CreateAccount/CreateAccount';
import Confirmation from '../../pages/CreateAccount/Confirmation';
import { Dashboard } from '../../pages/Dashboard/Dashboard';
import ResetPassword from '../../pages/ResetPassword/ResetPassword';
import ForgotPassword from '../../pages/ForgotPassword/ForgotPassword';
import ChangePassword from '../../pages/ChangePassword/ChangePassword';
import AccountActivation from '../../pages/AccountActivation/AccountActivation';
import ResumeProfile from '../../pages/ResumeProfile/ResumeProfile';
import { ResumePreview } from '../../pages/ResumePreview/ResumePreview';
import ResumeDashboard from '../../pages/ResumePreview/ResumeDasboard';
import MySkills from '../../pages/MySkills/MySkills';
import PublicResumeDashboard from '../../pages/ResumePreview/PublicResumeDashboard';
import MyApplications from '../../pages/MyApplications/MyApplications';
import SeniorLevelFlow from '../../pages/SeniorLevelFlow/SeniorLevelFlow';
import InterviewInvite from '../../pages/InterviewInvite/InterviewInvite';
import NotFound from '../../pages/NotFound/NotFound';
import GoogleSignInTest from '../../pages/GoogleSignInTest/GoogleSignInTest';
import Openings from '../../pages/Openings/Openings';
import OpeningDetails from '../../pages/OpeningDetails/OpeningDetails';
import Terms from '../../pages/Terms/Terms';
import Privacy from '../../pages/Privacy/Privacy';
import PrivateRoute from './PrivateRoute';
import NavigationSetter from '../../utils/NavigationSetter';
import PageViewTracker from '../../utils/PageViewTracker';

const RootEntryRoute = () => {
  const token = getToken();
  const user = getUser();
  const isAuthenticatedCandidate = Boolean(
    token && user?.roles?.includes('candidate')
  );

  if (isAuthenticatedCandidate) {
    return <Navigate to="/profile-dashboard" replace />;
  }

  if (!token) {
    return <Navigate to="/join-us" replace />;
  }

  return <AuthLayout />;
};

const NotFoundRedirect = () => {
  const token = getToken();

  if (!token) {
    return <Navigate to="/join-us" replace />;
  }

  return <Navigate to="/404" replace />;
};

const AppRouter = () => (
  <>
    <Toaster />
    <BrowserRouter>
      <NavigationSetter />
      <PageViewTracker />
      <Routes>
        <Route path="/" element={<MainLayout />}>
          {/* Public routes */}
          <Route index element={<RootEntryRoute />} />
          <Route path="join-us" element={<AuthLayout />} />
          {/* External signup links may include a token in the path */}
          <Route path="join-us/:token" element={<AuthLayout />} />
          <Route
            path="candidates/google/callback"
            element={<GoogleCallback />}
          />
          <Route path="resume/update" element={<PublicResumeDashboard />} />
          <Route path="confirm-interest" element={<SeniorLevelFlow />} />
          <Route path="interview" element={<InterviewInvite />} />
          <Route path="confirmation" element={<Confirmation />} />
          <Route path="set-password" element={<ResetPassword />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="google-signin-test" element={<GoogleSignInTest />} />
          <Route path="openings" element={<Openings />} />
          <Route path="openings/:id" element={<OpeningDetails />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFoundRedirect />} />

          {/* Protected routes */}
          <Route element={<PrivateRoute />}>
            <Route
              path="dashboard"
              element={<Navigate to="/profile-dashboard" replace />}
            />
            <Route path="create-link" element={<CreateAccount />} />
            <Route path="submit-resume" element={<SubmitResume />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="resume-preview" element={<ResumePreview />} />
            <Route path="profile-dashboard" element={<ResumeDashboard />} />
            <Route path="my-skills" element={<MySkills />} />
            <Route path="my-applications" element={<MyApplications />} />
            {/* Same components as the magic-link routes above, opened
                authenticated from the My Applications list instead of an
                email link — they detect this via the :secMappingId param. */}
            <Route
              path="my-applications/requests/:secMappingId"
              element={<SeniorLevelFlow />}
            />
            <Route
              path="my-applications/interviews/:secMappingId"
              element={<InterviewInvite />}
            />
            <Route path="activate-account" element={<AccountActivation />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  </>
);

export default AppRouter;
